import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import * as Icon from 'react-feather';
import { formatMoney, validateAddress } from '@loop-coffee/commerce-contracts';

import { itemCount, useCommerce } from '../../commerce/CommerceContext';
import { PaymentOptions } from '../../commerce/payments/PaymentOptions';
import { describePaymentStatus } from '../../commerce/payments/registry';
import { Alert, CheckoutShell, contactLink, mono, panel } from './ui';

const STEP_LABELS = { cart: 'carrinho', address: 'entrega', review: 'revisão', confirmation: 'confirmação' };

function Steps({ current }) {
    return (
        <ol className="d-flex flex-wrap gap-3 list-unstyled mb-4" aria-label="Etapas do checkout" style={{ fontFamily: mono, fontSize: 12 }}>
            {Object.entries(STEP_LABELS).map(([step, label]) => (
                <li key={step} aria-current={step === current ? 'step' : undefined}
                    style={{ color: step === current ? '#BE1E2D' : 'rgba(255,255,255,0.5)' }}>
                    {label}()
                </li>
            ))}
        </ol>
    );
}

function Totals({ cart }) {
    const money = (amount) => formatMoney(amount, cart.currency_code);
    return (
        <dl className="mb-0" style={{ fontSize: 14 }}>
            <div className="d-flex justify-content-between text-white-50">
                <dt className="fw-normal">Produtos</dt>
                <dd className="mb-1">{money(cart.item_total ?? cart.subtotal)}</dd>
            </div>
            {cart.shipping_methods?.length > 0 && (
                <div className="d-flex justify-content-between text-white-50">
                    <dt className="fw-normal">Frete</dt>
                    <dd className="mb-1">{money(cart.shipping_total)}</dd>
                </div>
            )}
            {cart.tax_total > 0 && (
                <div className="d-flex justify-content-between text-white-50">
                    <dt className="fw-normal">Impostos</dt>
                    <dd className="mb-1">{money(cart.tax_total)}</dd>
                </div>
            )}
            <div className="d-flex justify-content-between text-white fw-bold">
                <dt>Total</dt>
                <dd className="mb-0" data-testid="cart-total">{money(cart.total)}</dd>
            </div>
        </dl>
    );
}

function CartStep({ onNext }) {
    const { cart, status, notice, error, pending, restore, updateQuantity, removeItem } = useCommerce();

    if (status === 'loading') {
        return <p role="status" className="text-white-50">Carregando carrinho…</p>;
    }
    if (status === 'error') {
        return (
            <Alert>
                Não foi possível carregar seu carrinho ({error?.message}). Seus itens continuam salvos.
                <div className="mt-2">
                    <button type="button" className="btn btn-sm btn-outline-light" onClick={restore}>Tentar novamente</button>
                </div>
            </Alert>
        );
    }

    const items = cart?.items || [];
    return (
        <>
            {notice === 'expired' && (
                <Alert tone="info" role="status">
                    Seu carrinho anterior expirou ou já foi finalizado. Escolha os cafés novamente.
                </Alert>
            )}
            {error && <Alert>{error.message}</Alert>}
            {items.length === 0 ? (
                <div style={panel}>
                    <p className="text-white mb-3">Seu carrinho está vazio.</p>
                    <Link to="/#bitbean" className="btn btn-primary btn-sm">Ver cafés</Link>
                </div>
            ) : (
                <div style={panel}>
                    <ul className="list-unstyled mb-4" aria-label="Itens do carrinho">
                        {items.map((item) => (
                            <li key={item.id} className="d-flex flex-wrap justify-content-between align-items-center gap-3 py-3"
                                style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <div>
                                    <div className="text-white fw-semibold">{item.product_title || item.title}</div>
                                    <small className="text-white-50">
                                        {item.variant_title} · {formatMoney(item.unit_price, cart.currency_code)} / un.
                                    </small>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <button type="button" className="btn btn-sm btn-outline-light" disabled={pending}
                                        aria-label={`Diminuir ${item.title}`} onClick={() => updateQuantity(item.id, item.quantity - 1)}>
                                        <Icon.Minus size={14} />
                                    </button>
                                    <span className="text-white" aria-label={`Quantidade de ${item.title}`} style={{ minWidth: 24, textAlign: 'center' }}>
                                        {item.quantity}
                                    </span>
                                    <button type="button" className="btn btn-sm btn-outline-light" disabled={pending}
                                        aria-label={`Aumentar ${item.title}`} onClick={() => updateQuantity(item.id, item.quantity + 1)}>
                                        <Icon.Plus size={14} />
                                    </button>
                                    <button type="button" className="btn btn-sm btn-link text-white-50" disabled={pending}
                                        aria-label={`Remover ${item.title}`} onClick={() => removeItem(item.id)}>
                                        <Icon.Trash2 size={14} />
                                    </button>
                                </div>
                                <span className="text-white">{formatMoney(item.total ?? item.unit_price * item.quantity, cart.currency_code)}</span>
                            </li>
                        ))}
                    </ul>
                    <Totals cart={cart} />
                    <button type="button" className="btn btn-primary w-100 mt-4" disabled={pending} onClick={onNext}>
                        Continuar para entrega
                    </button>
                </div>
            )}
        </>
    );
}

const ADDRESS_INPUTS = [
    { name: 'email', label: 'E-mail', type: 'email', autoComplete: 'email' },
    { name: 'first_name', label: 'Nome', autoComplete: 'given-name' },
    { name: 'last_name', label: 'Sobrenome', autoComplete: 'family-name' },
    // The carrier label and the Asaas charge both need the buyer's document.
    { name: 'cpf_cnpj', label: 'CPF ou CNPJ', inputMode: 'numeric', autoComplete: 'off' },
    { name: 'phone', label: 'Telefone (opcional)', type: 'tel', autoComplete: 'tel' },
    { name: 'postal_code', label: 'CEP', autoComplete: 'postal-code' },
    { name: 'address_1', label: 'Endereço e número', autoComplete: 'address-line1' },
    { name: 'address_2', label: 'Complemento (opcional)', autoComplete: 'address-line2' },
    { name: 'city', label: 'Cidade', autoComplete: 'address-level2' },
    { name: 'province', label: 'Estado (UF)', autoComplete: 'address-level1' },
];

function AddressStep({ onBack, onNext }) {
    const { cart, error, pending, setAddress } = useCommerce();
    const [values, setValues] = useState(() => {
        const saved = cart?.shipping_address || {};
        const savedValue = (name) => (name === 'email' ? cart?.email : name === 'cpf_cnpj' ? saved.metadata?.cpf_cnpj : saved[name]);
        return Object.fromEntries(ADDRESS_INPUTS.map(({ name }) => [name, savedValue(name) || '']));
    });
    const [errors, setErrors] = useState({});

    const submit = async (event) => {
        event.preventDefault();
        const found = validateAddress(values);
        setErrors(found);
        if (Object.keys(found).length) return;
        if (await setAddress(values)) onNext();
    };

    return (
        <form noValidate onSubmit={submit} style={panel} aria-label="Endereço de entrega">
            {error && <Alert>{error.message}</Alert>}
            <Row>
                {ADDRESS_INPUTS.map(({ name, label, type = 'text', autoComplete, inputMode }) => (
                    <Col md={6} key={name} className="mb-3">
                        <label htmlFor={`address-${name}`} className="form-label text-white-50" style={{ fontSize: 13 }}>{label}</label>
                        <input
                            id={`address-${name}`}
                            name={name}
                            type={type}
                            autoComplete={autoComplete}
                            inputMode={inputMode}
                            className={`form-control${errors[name] ? ' is-invalid' : ''}`}
                            value={values[name]}
                            aria-invalid={Boolean(errors[name])}
                            aria-describedby={errors[name] ? `address-${name}-error` : undefined}
                            onChange={(e) => { const { value } = e.target; setValues((prev) => ({ ...prev, [name]: value })); }}
                        />
                        {errors[name] && <div id={`address-${name}-error`} className="invalid-feedback">{errors[name]}</div>}
                    </Col>
                ))}
            </Row>
            <div className="d-flex gap-2 mt-2">
                <button type="button" className="btn btn-outline-light" onClick={onBack}>Voltar ao carrinho</button>
                <button type="submit" className="btn btn-primary flex-grow-1" disabled={pending}>Revisar pedido</button>
            </div>
        </form>
    );
}

// 0 days: local delivery or pickup the same day.
const leadTime = (days) => (days === 0 ? 'hoje' : days === 1 ? '1 dia útil' : `${days} dias úteis`);

// `onStatus` tells the review step whether shipping is priced (`ready`), so the order cannot be
// placed on top of a loading, failed or empty quote.
function ShippingSlot({ onStatus }) {
    const { cart, shipping, selectShipping, pending } = useCommerce();
    const [state, setState] = useState({ status: 'loading', options: [] });
    const [attempt, setAttempt] = useState(0);
    const cartId = cart?.id;
    // The quote depends on the destination postal code and on the items (weight): re-quote when either changes.
    const postalCode = cart?.shipping_address?.postal_code || '';
    const itemsKey = (cart?.items || []).map((item) => `${item.variant_id}:${item.quantity}`).join(',');

    useEffect(() => {
        let active = true;
        setState({ status: 'loading', options: [] });
        shipping.listOptions({ id: cartId }).then(
            (options) => active && setState({ status: options.length ? 'ready' : 'empty', options }),
            () => active && setState({ status: 'error', options: [] }),
        );
        return () => { active = false; };
    }, [shipping, cartId, postalCode, itemsKey, attempt]);

    useEffect(() => { onStatus?.(state.status); }, [onStatus, state.status]);

    const selected = cart.shipping_methods?.[0]?.shipping_option_id;

    if (state.status === 'loading') return <p role="status" className="text-white-50">Calculando opções de envio…</p>;
    if (state.status === 'error') {
        return (
            <Alert>
                Não foi possível calcular o frete para este endereço.{' '}
                <button type="button" className="btn btn-sm btn-outline-light ms-2" onClick={() => setAttempt((n) => n + 1)}>
                    Tentar novamente
                </button>
            </Alert>
        );
    }
    if (state.status === 'empty') return <Alert>Nenhuma opção de envio para este endereço.</Alert>;

    return (
        <fieldset className="mb-3">
            <legend className="text-white" style={{ fontSize: 15 }}>Envio</legend>
            {/* Clicking the option already checked re-quotes it (onChange does not fire), e.g. after "Recalcule o frete". */}
            {state.options.map((option) => (
                <div className="form-check" key={option.id}>
                    <input className="form-check-input" type="radio" name="shipping-option" id={`shipping-${option.id}`}
                        checked={selected === option.id} disabled={pending} onChange={() => selectShipping(option.id)}
                        onClick={() => selected === option.id && selectShipping(option.id)} />
                    <label className="form-check-label text-white-50" htmlFor={`shipping-${option.id}`}>
                        {option.label} — {option.amount === 0 ? 'Grátis' : formatMoney(option.amount, cart.currency_code)}
                        {typeof option.deliveryDays === 'number' ? ` · ${leadTime(option.deliveryDays)}` : ''}
                    </label>
                </div>
            ))}
        </fieldset>
    );
}

function PaymentSlot() {
    const { listPaymentOptions, payment, selectPayment, pending } = useCommerce();
    const [state, setState] = useState({ status: 'loading', options: [] });

    useEffect(() => {
        let active = true;
        setState({ status: 'loading', options: [] });
        listPaymentOptions().then(
            ({ options }) => {
                if (!active) return;
                setState({ status: 'ready', options });
                if (options.length === 1) selectPayment(options[0].id);
            },
            () => active && setState({ status: 'error', options: [] }),
        );
        return () => { active = false; };
    }, [listPaymentOptions, selectPayment]);

    if (state.status === 'loading') return <p role="status" className="text-white-50">Carregando formas de pagamento…</p>;
    if (state.status === 'error') return <Alert>Não foi possível carregar as formas de pagamento.</Alert>;

    return (
        <div data-testid="payment-slot" data-provider={payment?.id || ''} style={{ fontSize: 14 }}>
            <PaymentOptions options={state.options} selectedId={payment?.id} onSelect={selectPayment} disabled={pending} />
        </div>
    );
}

function ReviewStep({ onBack, onEditCart, onPlaced }) {
    const { cart, error, pending, payment, placeOrder } = useCommerce();
    const [shippingStatus, setShippingStatus] = useState('loading');

    if (!cart?.items?.length) {
        return (
            <Alert>
                Seu carrinho mudou e está vazio.{' '}
                <button type="button" className="btn btn-sm btn-outline-light ms-2" onClick={onEditCart}>Voltar ao carrinho</button>
            </Alert>
        );
    }

    const address = cart.shipping_address || {};
    const place = async () => {
        if (await placeOrder()) onPlaced();
    };

    return (
        <div style={panel}>
            {error && (
                <Alert>
                    {error.message} Os preços e o estoque foram conferidos novamente pela loja.
                    <div className="mt-2">
                        <button type="button" className="btn btn-sm btn-outline-light" onClick={onEditCart}>Revisar carrinho</button>
                    </div>
                </Alert>
            )}
            <h2 className="text-white h6">Itens</h2>
            <ul className="list-unstyled text-white-50 mb-3" style={{ fontSize: 14 }}>
                {cart.items.map((item) => (
                    <li key={item.id}>{item.quantity}× {item.product_title || item.title} — {formatMoney(item.total ?? item.unit_price * item.quantity, cart.currency_code)}</li>
                ))}
            </ul>
            <h2 className="text-white h6">Entrega</h2>
            <p className="text-white-50 mb-3" style={{ fontSize: 14 }}>
                {address.first_name} {address.last_name} · {address.address_1}{address.address_2 ? `, ${address.address_2}` : ''} · {address.city}/{address.province?.toUpperCase()} · {address.postal_code}
                <br />
                <button type="button" className="btn btn-link btn-sm p-0" onClick={onBack}>Alterar endereço</button>
            </p>
            <ShippingSlot onStatus={setShippingStatus} />
            <h2 className="text-white h6">Pagamento</h2>
            <PaymentSlot />
            <Totals cart={cart} />
            <button type="button" className="btn btn-primary w-100 mt-4"
                disabled={pending || shippingStatus !== 'ready' || !cart.shipping_methods?.length || !payment} onClick={place}>
                {pending ? 'Enviando…' : 'Finalizar pedido'}
            </button>
        </div>
    );
}

function Confirmation() {
    const { order, payment, redirectUrl } = useCommerce();
    if (payment?.flow === 'redirect' && redirectUrl) {
        return (
            <div style={panel} role="status">
                <h2 className="text-white h4">Pedido {order?.display_id ? `#${order.display_id}` : ''} criado</h2>
                <p className="text-white-50">Levando você ao ambiente de pagamento…</p>
                <a href={redirectUrl} className="btn btn-primary btn-sm">Ir para o pagamento</a>
            </div>
        );
    }
    const status = describePaymentStatus(order?.payment_status);
    return (
        <div style={panel} role="status">
            <Icon.CheckCircle size={32} color="#BE1E2D" className="mb-3" />
            <h2 className="text-white h4">Pedido {order?.display_id ? `#${order.display_id}` : ''} recebido</h2>
            <p className="text-white" data-testid="payment-status" data-state={status.state}>{status.label}</p>
            <p className="text-white-50">
                {status.isPaid ? '' : 'Avisaremos assim que o pagamento for confirmado. '}
                Enviaremos as próximas etapas para {order?.email}.
                {payment?.testOnly && ' Ambiente de teste: nenhuma cobrança real foi feita.'}
            </p>
            <Link to="/" className="btn btn-outline-light btn-sm">Voltar à loja</Link>
        </div>
    );
}

function Unavailable() {
    return (
        <div style={panel}>
            <p className="text-white">A compra online ainda não está disponível.</p>
            <a href={contactLink} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">Comprar pelo WhatsApp</a>
        </div>
    );
}

export default function Checkout() {
    const { enabled, cart } = useCommerce();
    const [step, setStep] = useState('cart');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    // Address and review need an open cart; an expired/emptied cart sends the customer back.
    const current = step !== 'confirmation' && step !== 'cart' && !cart ? 'cart' : step;

    return (
        <CheckoutShell title={(
            <>Checkout {enabled && current === 'cart' && cart ? <small className="text-white-50 fs-6">({itemCount(cart)} itens)</small> : null}</>
        )}>
            {!enabled ? <Unavailable /> : (
                <>
                    <Steps current={current} />
                    {current === 'cart' && <CartStep onNext={() => setStep('address')} />}
                    {current === 'address' && <AddressStep onBack={() => setStep('cart')} onNext={() => setStep('review')} />}
                    {current === 'review' && (
                        <ReviewStep onBack={() => setStep('address')} onEditCart={() => setStep('cart')}
                            onPlaced={() => setStep('confirmation')} />
                    )}
                    {current === 'confirmation' && <Confirmation />}
                </>
            )}
        </CheckoutShell>
    );
}
